// Chakra Imports
import {
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  Heading,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
// Custom Components
import { ItemContent } from 'components/Menu/ItemContent';
import { SearchBar } from 'components/Navbar/searchBar/SearchBar';
import { SidebarResponsive } from 'components/Sidebar/Sidebar';
import TabGroup from 'components/TabGroup/TabGroup';
import ItemGrid from 'components/Item/ItemGrid/ItemGrid';
import ListingGrid from 'components/Listing/ListingGrid/ListingGrid';
import ArtistGrid from 'components/Artist/ArtistGrid/ArtistGrid';
// Assets
import navImage from 'assets/img/layout/Navbar.png';
import { MdNotificationsNone, MdInfoOutline, MdOutlinePalette } from 'react-icons/md';
import { ImStatsDots } from 'react-icons/im';
import { RiAuctionLine } from 'react-icons/ri';
import Configurator from 'components/Navbar/Configurator';
import routes from 'routes';
import { findActiveRoute } from 'utils/url';
import { useAuth } from 'contexts/AuthContext';
import { AuthApiClient } from 'api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useItemSearch, useListingSearch } from 'hooks/queries/useSearch';
import { useArtists } from 'hooks/queries/useArtists';
import { NavigationService } from 'services/Navigation';
import { ListingModel } from 'models/Listing/Listing';
import { ArtistModel } from 'models/Artist/Artist';
import { useCreateAction } from 'contexts/CreateActionContext';

export default function HeaderLinks(props: {
  secondary: boolean;
  [x: string]: any;
}) {
  const { secondary, theme, setTheme } = props;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSearchTab, setSelectedSearchTab] = useState<number>(0);

  // Chakra Color Mode
  const navbarIcon = useColorModeValue('gray.400', 'white');
  let menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorBrand = useColorModeValue('brand.700', 'brand.400');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const actionBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const actionIconBg = useColorModeValue('white', 'navy.800');
  const actionColor = useColorModeValue('gray.700', 'white');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  const borderButton = useColorModeValue('secondaryGray.500', 'whiteAlpha.200');

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const createAction = useCreateAction();

  // Search hooks - only execute when query length > 3
  const shouldSearch = searchQuery.length > 3;
  const { data: itemSearchData, isLoading: itemSearchLoading } = useItemSearch(
    shouldSearch ? { query: searchQuery } : undefined
  );
  const { data: listingSearchData, isLoading: listingSearchLoading } = useListingSearch(
    shouldSearch ? { query: searchQuery } : undefined
  );
  const { data: artistSearchData = [], isLoading: artistSearchLoading } = useArtists();

  const filteredArtists = useMemo<ArtistModel[]>(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return artistSearchData;
    }

    return artistSearchData.filter((artist) => {
      const nameMatch = artist.name.toLowerCase().includes(query);
      const aliasMatch = artist.aliases?.some((alias) => alias.toLowerCase().includes(query));
      return nameMatch || aliasMatch;
    });
  }, [artistSearchData, searchQuery]);

  const onLogout = () => {
    AuthApiClient.logout().then(() => {
      logout();
      navigate('/auth/sign-in');
    });
  };

  const onSearch = (query: string) => {
    setSearchQuery(query);
  };

  const onItemClick = (itemId: string, itemName: string) => {
    navigate(NavigationService.getItemDetailsPath(itemId, itemName));
  };

  const onListingClick = (listing: ListingModel) => {
    navigate(NavigationService.getListingDetailsPath(listing.id, listing.getDisplayName()));
  };

  const renderItemSearchResults = () => {
    return (
      <Box paddingRight={8} maxW={{base: '80vw', md: '25vw'}}>
        <ItemGrid
          items={itemSearchData?.data || []}
          isLoading={itemSearchLoading}
          showFilters={false}
          gridDisplay={true}
          gridMaxHeight={500}
          onItemCardClick={(item) => onItemClick(item.id, item.getDisplayName())}
        />
      </Box>
    );
  };

  const renderListingSearchResults = () => {
    return (
      <Box paddingRight={8} maxW={{base: '80vw', md: '25vw'}}>
        <ListingGrid
          gridListings={listingSearchData?.data || []}
          isLoading={listingSearchLoading}
          showFilters={false}
          showAssociatedItems={false}
          onListingClick={onListingClick}
          compactMode={true}
        />
      </Box>
    );
  };

  const renderArtistSearchResults = () => {
    return (
      <Box paddingRight={8} maxW={{base: '80vw', md: '25vw'}}>
        <ArtistGrid
          artists={filteredArtists}
          isLoading={artistSearchLoading}
          showFilters={false}
          showSort={false}
          gridDisplay={true}
          gridMaxHeight={500}
          onArtistCardClick={(artist) => navigate(NavigationService.getArtistDetailsPath(artist.id, artist.name))}
        />
      </Box>
    );
  };

  const activeRoute = useMemo(() => findActiveRoute(location.pathname, routes), [location.pathname]);

  const renderActionControl = () => {
    if (!secondary) {
      return null;
    }

    if (createAction) {
      const { icon, label, onClick, isDisabled, isLoading } = createAction;

      return (
        <Button
          ms="auto"
          me="6px"
          borderRadius="20px"
          h="35px"
          px="16px"
          colorScheme="brand"
          onClick={onClick}
          isDisabled={isDisabled}
          isLoading={isLoading}
          leftIcon={icon ? <Icon as={icon} w="16px" h="16px" /> : undefined}
        >
          {label ?? 'Create'}
        </Button>
      );
    }

    if (activeRoute?.icon) {
      return (
        <Flex
          bg={actionBg}
          borderRadius="20px"
          ms="auto"
          me="6px"
          align="center"
          px="10px"
          py="6px"
        >
          <Flex
            align="center"
            justify="center"
            bg={actionIconBg}
            h="29px"
            w="29px"
            borderRadius="20px"
            me="7px"
          >
            {React.cloneElement(activeRoute.icon, {
              color: actionColor,
              w: '16px',
              h: '16px',
            })}
          </Flex>
          <Text
            w="max-content"
            color={actionColor}
            fontSize="sm"
            fontWeight="700"
            display={{ base: 'none', md: 'inline-flex' }}
          >
            {activeRoute.name}
          </Text>
        </Flex>
      );
    }

    return null;
  };

  const renderSearchResults = () => {
    const labels = [
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="items-tab">
        <Icon as={ImStatsDots} />
        <Text>Items</Text>
      </Flex>,
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="listings-tab">
        <Icon as={RiAuctionLine} />
        <Text>Past Sales</Text>
      </Flex>,
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="artists-tab">
        <Icon as={MdOutlinePalette} />
        <Text>Artists</Text>
      </Flex>
    ];

    const content = [
      renderItemSearchResults(),
      renderListingSearchResults(),
      renderArtistSearchResults()
    ];

    return <TabGroup labels={labels} content={content} startingTab={selectedSearchTab} onChange={setSelectedSearchTab} />;
  };

  return (
    <Flex
      w={{ sm: '100%', md: 'auto' }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
      p="10px"
      borderRadius="999px"
      boxShadow={shadow}
    >
      <SearchBar
        mb={() => {
          if (secondary) {
            return { base: '10px', md: 'unset' };
          }
          return 'unset';
        }}
        me="10px"
        borderRadius="20px"
        onChange={onSearch}
        showResultBox={true}
        hasValidResults={true}
        results={renderSearchResults()}
      />
      {renderActionControl()}
      <SidebarResponsive routes={routes} />
      <Menu>
        <MenuButton p="0px">
          <Icon
            mt="6px"
            as={MdNotificationsNone}
            color={navbarIcon}
            w="18px"
            h="18px"
            me="10px"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="20px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
          mt="22px"
          me={{ base: '30px', md: 'unset' }}
          minW={{ base: 'unset', md: '400px', xl: '450px' }}
          maxW={{ base: '360px', md: 'unset' }}
        >
          <Flex w="100%" mb="20px">
            <Text fontSize="md" fontWeight="600" color={textColor}>
              Notifications
            </Text>
            <Text
              fontSize="sm"
              fontWeight="500"
              color={textColorBrand}
              ms="auto"
              cursor="pointer"
            >
              Mark all read
            </Text>
          </Flex>
          <Flex flexDirection="column">
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              px="0"
              borderRadius="8px"
              mb="10px"
            >
              <ItemContent info="Horizon UI Dashboard PRO" />
            </MenuItem>
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              px="0"
              borderRadius="8px"
              mb="10px"
            >
              <ItemContent info="Horizon Design System Free" />
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>

      <Menu>
        <MenuButton p="0px">
          <Icon
            mt="6px"
            as={MdInfoOutline}
            color={navbarIcon}
            w="18px"
            h="18px"
            me="10px"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="20px"
          me={{ base: '30px', md: 'unset' }}
          borderRadius="20px"
          bg={menuBg}
          border="none"
          mt="22px"
          minW={{ base: 'unset' }}
          maxW={{ base: '360px', md: 'unset' }}
        >
          <Image src={navImage} borderRadius="16px" mb="28px" />
          <Flex flexDirection="column">
            <Link w="100%" href="https://horizon-ui.com/pro">
              <Button w="100%" h="44px" mb="10px" variant="brand">
                Buy Horizon UI PRO
              </Button>
            </Link>
            <Link
              w="100%"
              href="https://horizon-ui.com/documentation/docs/introduction"
            >
              <Button
                w="100%"
                h="44px"
                mb="10px"
                border="1px solid"
                bg="transparent"
                borderColor={borderButton}
              >
                See Documentation
              </Button>
            </Link>
            <Link
              w="100%"
              href="https://github.com/horizon-ui/horizon-ui-chakra-ts"
            >
              <Button
                w="100%"
                h="44px"
                variant="no-hover"
                color={textColor}
                bg="transparent"
              >
                Try Horizon Free
              </Button>
            </Link>
          </Flex>
        </MenuList>
      </Menu>
      <Configurator
        mini={props.mini}
        setMini={props.setMini}
        theme={theme}
        setTheme={setTheme}
      />

      <Menu>
        <MenuButton p="0px">
          <Avatar
            _hover={{ cursor: 'pointer' }}
            color={textColor}
            name="Adela Parkson"
            bg="#11047A"
            size="sm"
            w="40px"
            h="40px"
            src={currentUser?.image}
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="0px"
          mt="10px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
        >
          <Flex w="100%" mb="0px">
            <Text
              ps="20px"
              pt="16px"
              pb="10px"
              w="100%"
              borderBottom="1px solid"
              borderColor={borderColor}
              fontSize="sm"
              fontWeight="700"
              color={textColor}
            >
              👋&nbsp; Hey, { currentUser?.name }
            </Text>
          </Flex>
          <Flex flexDirection="column" p="10px">
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm">Profile Settings</Text>
            </MenuItem>
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm">Newsletter Settings</Text>
            </MenuItem>
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              color="red.400"
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm" onClick={ onLogout }>Log out</Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
    </Flex>
  );
}
